import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Typography,
  Box,
  Paper,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Modal,
  FormControlLabel,
  Switch,
  CircularProgress,
} from "@mui/material";
import { ArrowBack, Edit, Refresh } from "@mui/icons-material";
import { format } from "date-fns";
import { Timestamp as FirestoreTimestamp } from "firebase/firestore";
import {
  fetchUserDetails,
  fetchUserEvents,
  fetchAllOrganizations,
  updateUserSuperAdmin,
  crmSliceSelector,
} from "../../../redux/features/crmSlice";
import { usersSliceSelector } from "../../../redux/features/usersSlice";
import { statusForEvent } from "../../../redux/features/adminEventsSlice";
import EventStatusView from "../../AdminEvents/EventsList/EventListItem/components/EventStatusView";

export default function CRMUserDetails() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const crmState = useSelector((state) => crmSliceSelector(state));
  const usersState = useSelector((state) => usersSliceSelector(state));
  const {
    userDetails,
    userDetailsLoading,
    userEventsWithEnhancedTicketTypes,
    userEventsLoading,
    userEventStats,
    organizationsMap,
    organizationsFetched,
  } = crmState;
  const currentUser = usersState.user;
  const [editSuperAdminModalOpen, setEditSuperAdminModalOpen] = useState(false);
  const [superAdminValue, setSuperAdminValue] = useState(false);
  const [updating, setUpdating] = useState(false);

  const user = userDetails[userId];
  const eventsMap = userEventsWithEnhancedTicketTypes[userId];
  const eventStatsMap = userEventStats[userId] || {};
  const isLoading = userDetailsLoading[userId] || userEventsLoading[userId];
  const isCurrentUser = currentUser?.id === userId;

  // Track what we've attempted to fetch to prevent infinite loops
  const fetchAttemptedRef = useRef({
    details: false,
    events: false,
  });

  useEffect(() => {
    if (!userId) return;

    // Reset fetch attempts when userId changes
    fetchAttemptedRef.current = {
      details: false,
      events: false,
    };
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    // Fetch organizations first if needed (for displaying org names)
    if (!organizationsFetched) {
      dispatch(fetchAllOrganizations());
    }

    // Only fetch if not already loading, data doesn't exist, and we haven't attempted yet
    if (
      user === undefined &&
      !userDetailsLoading[userId] &&
      !fetchAttemptedRef.current.details
    ) {
      fetchAttemptedRef.current.details = true;
      dispatch(fetchUserDetails({ userId }));
    }
  }, [userId, user, userDetailsLoading, organizationsFetched, dispatch]);

  useEffect(() => {
    if (!userId) return;

    // Only fetch if not already loading, data doesn't exist, and we haven't attempted yet
    if (
      eventsMap === undefined &&
      !userEventsLoading[userId] &&
      !fetchAttemptedRef.current.events
    ) {
      fetchAttemptedRef.current.events = true;
      dispatch(fetchUserEvents({ userId }));
    }
  }, [userId, eventsMap, userEventsLoading, dispatch]);

  if (isLoading && !user) {
    return (
      <Container>
        <LinearProgress sx={{ marginTop: 2 }} />
      </Container>
    );
  }

  if (!user) {
    return (
      <Container>
        <Typography variant="h2" paddingTop={"20px"}>
          User Not Found
        </Typography>
      </Container>
    );
  }

  const { displayName, email, superAdmin, createdAt, lastLogin } = user;

  // Build organizations list from organizationsMap
  const userOrganizations = [];
  Object.keys(organizationsMap || {}).forEach((orgId) => {
    const org = organizationsMap[orgId];
    const roles = [];
    if (org.owner === userId) {
      roles.push("Owner");
    }
    if (org.admins && org.admins.includes(userId)) {
      roles.push("Admin");
    }
    if (org.scanners && org.scanners.includes(userId)) {
      roles.push("Scanner");
    }
    if (roles.length > 0) {
      userOrganizations.push({
        organizationId: orgId,
        organizationName: org.name || "Unnamed Organization",
        roles,
      });
    }
  });

  const createdDate = createdAt?.toDate
    ? format(createdAt.toDate(), "MMM d, yyyy")
    : "N/A";

  let lastLoginDate = "Never";
  if (lastLogin) {
    let lastLoginTimestamp = lastLogin;
    if (lastLogin.seconds && lastLogin.nanoseconds !== undefined) {
      lastLoginTimestamp = new FirestoreTimestamp(
        lastLogin.seconds,
        lastLogin.nanoseconds,
      );
    }
    if (lastLoginTimestamp?.toDate) {
      lastLoginDate = format(lastLoginTimestamp.toDate(), "MMM d, yyyy h:mm a");
    }
  }

  const eventsList = eventsMap ? Object.values(eventsMap) : [];
  const sortedEvents = eventsList.sort((a, b) => {
    const aDate = a.createdAt?.toMillis?.() || 0;
    const bDate = b.createdAt?.toMillis?.() || 0;
    return bDate - aDate;
  });

  const handleRefresh = () => {
    // Reset fetch attempts to allow refetching
    fetchAttemptedRef.current = {
      details: false,
      events: false,
    };
    // Force refetch
    dispatch(fetchUserDetails({ userId }));
    dispatch(fetchUserEvents({ userId }));
  };

  return (
    <Container>
      <Box marginTop={2} marginBottom={2} display="flex" gap={2}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/crm/users")}
        >
          Back to Users
        </Button>
        <Button
          startIcon={<Refresh />}
          onClick={handleRefresh}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </Box>

      {isLoading && <LinearProgress sx={{ marginTop: 2, marginBottom: 2 }} />}

      <Typography variant="h2" paddingTop={"20px"}>
        {displayName || email || "Unnamed User"}
      </Typography>

      {/* User Information Section */}
      <Box marginTop={4}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            User Information
          </Typography>
          <Box marginTop={2}>
            <Typography variant="body2" color="text.secondary">
              User ID
            </Typography>
            <Typography variant="body1" marginBottom={2}>
              {userId}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Email
            </Typography>
            <Typography variant="body1" marginBottom={2}>
              {email || "N/A"}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Display Name
            </Typography>
            <Typography variant="body1" marginBottom={2}>
              {displayName || "N/A"}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Super Admin
            </Typography>
            <Box display="flex" alignItems="center" gap={1} marginBottom={2}>
              <Typography variant="body1">
                {superAdmin ? "Yes" : "No"}
              </Typography>
              {!isCurrentUser && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setSuperAdminValue(superAdmin || false);
                    setEditSuperAdminModalOpen(true);
                  }}
                  sx={{ padding: 0.5 }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              )}
            </Box>

            <Typography variant="body2" color="text.secondary">
              Created
            </Typography>
            <Typography variant="body1" marginBottom={2}>
              {createdDate}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Last Login
            </Typography>
            <Typography variant="body1">{lastLoginDate}</Typography>
          </Box>
        </Paper>
      </Box>

      {/* Organizations Section */}
      <Box marginTop={4}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Organizations
          </Typography>
          {userOrganizations.length === 0 ? (
            <Typography variant="body2" color="text.secondary" marginTop={2}>
              No organizations found
            </Typography>
          ) : (
            <TableContainer sx={{ marginTop: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Organization</TableCell>
                    <TableCell>Roles</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userOrganizations.map((org) => {
                    return (
                      <TableRow key={org.organizationId}>
                        <TableCell>
                          <Typography
                            variant="body2"
                            component={Link}
                            to={`/crm/organizations/${org.organizationId}`}
                            sx={{
                              textDecoration: "none",
                              color: "primary.main",
                            }}
                          >
                            {org.organizationName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {org.roles.join(", ")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Events Section */}
      <Box marginTop={4}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Events
          </Typography>
          {userEventsLoading[userId] ? (
            <LinearProgress sx={{ marginTop: 2 }} />
          ) : sortedEvents.length === 0 ? (
            <Typography variant="body2" color="text.secondary" marginTop={2}>
              No events found
            </Typography>
          ) : (
            <TableContainer sx={{ marginTop: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Organization</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Starts</TableCell>
                    <TableCell>Ends</TableCell>
                    <TableCell>Role</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedEvents.map((event) => {
                    const startsDate = event.startsAt?.toDate
                      ? format(event.startsAt.toDate(), "MMM d, yyyy h:mm a")
                      : "N/A";
                    const endsDate = event.endsAt?.toDate
                      ? format(event.endsAt.toDate(), "MMM d, yyyy h:mm a")
                      : "N/A";

                    // Merge stats into event for statusForEvent (ticket types already enhanced by selector)
                    const stats = eventStatsMap[event.id];
                    const eventWithStats = {
                      ...event,
                      stats,
                    };

                    // Determine user's role in this event
                    const isAdmin = event.admins?.includes(userId);
                    const isScanner = event.scanners?.includes(userId);
                    let role = "N/A";
                    if (isAdmin && isScanner) {
                      role = "Admin, Scanner";
                    } else if (isAdmin) {
                      role = "Admin";
                    } else if (isScanner) {
                      role = "Scanner";
                    }

                    const organization = organizationsMap[event.organizationId];

                    return (
                      <TableRow key={event.id}>
                        <TableCell>
                          <Typography variant="body2">
                            {event.name || "Unnamed Event"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            component={Link}
                            to={`/crm/organizations/${event.organizationId}`}
                            sx={{
                              textDecoration: "none",
                              color: "primary.main",
                            }}
                          >
                            {organization?.name || "Unknown Organization"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <EventStatusView
                            status={statusForEvent(eventWithStats).status}
                            squareLocationError={event.squareLocationError}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{startsDate}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{endsDate}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{role}</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Edit Super Admin Modal */}
      <Modal
        open={editSuperAdminModalOpen}
        onClose={() => setEditSuperAdminModalOpen(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 400 },
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" marginBottom={2}>
            Edit Super Admin Status
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={superAdminValue}
                onChange={(e) => setSuperAdminValue(e.target.checked)}
                disabled={updating}
              />
            }
            label="Super Admin"
          />
          <Box marginTop={3} display="flex" gap={2} justifyContent="flex-end">
            <Button
              onClick={() => setEditSuperAdminModalOpen(false)}
              disabled={updating}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                if (!currentUser?.id) {
                  alert("Unable to determine current user");
                  return;
                }
                setUpdating(true);
                try {
                  await dispatch(
                    updateUserSuperAdmin({
                      userId,
                      superAdmin: superAdminValue,
                      grantedByUserId: superAdminValue ? currentUser.id : null,
                    }),
                  ).unwrap();
                  setEditSuperAdminModalOpen(false);
                } catch (error) {
                  alert(
                    `Failed to update super admin status: ${error.message}`,
                  );
                } finally {
                  setUpdating(false);
                }
              }}
              disabled={updating}
              startIcon={updating ? <CircularProgress size={16} /> : null}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
}
